import { useEffect, useState } from "react";
import { api } from "./api-client.js";
import type { AdminQuoteListResponse } from "../types/admin-quotes.js";

/**
 * Pending-quotes badge counter — polls `GET /v1/admin/quotes?status=requested&pageSize=1`
 * every 60 s. Mirrors `useApplicationsBadge` (module-level cache +
 * subscriber list); the badge counter is read from the list response's
 * `total` field rather than counting `items` (pageSize=1 keeps the
 * payload minimal).
 *
 * Per plan T10 step 5: "poll GET /v1/admin/quotes?status=requested&pageSize=1
 * every 60s; show count of pending". Source endpoint chosen over the
 * dashboard's /v1/admin/dashboard/today aggregator because the dashboard
 * doesn't surface a `pending_quotes_count` field today (it would
 * require an api change owned by a future plan).
 */
let cached: number | null = null;
let refetchers: ((n: number) => void)[] = [];
let timer: ReturnType<typeof setInterval> | null = null;

const POLL_MS = 60 * 1000;

async function refresh(): Promise<void> {
  try {
    const data = await api.get<AdminQuoteListResponse>(
      "/v1/admin/quotes?status=requested&pageSize=1",
    );
    const count = data?.total;
    if (typeof count !== "number") return;
    cached = count;
    refetchers.forEach((f) => f(cached!));
  } catch {
    // Silent — same shape as useApplicationsBadge; a failing badge
    // never crashes the shell.
  }
}

/**
 * Hook: returns the current pending-quotes count from the admin quote
 * queue endpoint. The first mounted subscriber triggers a single fetch
 * + sets up the 60 s polling timer; subsequent mounts re-use the
 * cached value immediately. The polling timer is torn down when the
 * last subscriber unmounts.
 */
export function useQuotesBadge(): { count: number } {
  const [count, setCount] = useState(cached ?? 0);

  useEffect(() => {
    refetchers.push(setCount);
    if (cached !== null) setCount(cached);
    else void refresh();

    // Start the polling timer on the FIRST subscriber.
    if (refetchers.length === 1 && timer === null) {
      timer = setInterval(() => {
        void refresh();
      }, POLL_MS);
    }

    return () => {
      refetchers = refetchers.filter((f) => f !== setCount);
      // Stop polling when the last subscriber unmounts.
      if (refetchers.length === 0 && timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
  }, []);

  return { count };
}

/**
 * Imperatively clear the cache without triggering a refetch — useful
 * in tests + integration scenarios where the cache may be stale.
 */
export function invalidateQuotesBadgeCache(): void {
  cached = null;
}
